import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import type { Integration } from '@/types/integration';

const fetchIntegrations = async (): Promise<Integration[]> => {
  const response = await api.get<Integration[]>('/dashboard/integrations');
  return response.data;
};

export const useIntegrationsQuery = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useQuery<Integration[], Error>({
    enabled,
    queryKey: ['integrations'],
    queryFn: fetchIntegrations,
    // A mailbox is connected out-of-band: the OAuth flow leaves the app and
    // lands back on /settings/integrations, so any other surface that gates on
    // mailbox status (the invitation channel step, the bulk email modal) must
    // revalidate when it mounts rather than trust a stale cache — otherwise the
    // freshly connected mailbox stays invisible until a hard refresh.
    refetchOnMount: 'always',
    staleTime: 1000 * 30, // 30 seconds
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

export const useShopifyIntegrationQuery = (options: { enabled?: boolean } = {}) => {
  const { data: integrations, ...rest } = useIntegrationsQuery(options);

  const shopifyIntegration = integrations?.find(
    (integration) => integration.platform === 'shopify'
  );

  return {
    ...rest,
    data: shopifyIntegration,
  };
};
